import { useTranslation } from 'next-i18next';
import { useSiteInfo } from '@contexts/SiteInfoContext';
import { HeaderContext } from '@contexts/HeaderContext';
import React, {
    forwardRef, useContext, useEffect, useState,
} from 'react';
import { gotoAppDownload, gotoWAP } from '../../utils/login';
import {
    platformList, BDLoginPlatformList, LAYOUT_POSITIONS, appDownloadPosition,
} from '../../utils/dropdownList';
import { getCookie, setCookie } from '../../utils/cookieUtils';
import { getUserDevice, detectOperatingSystem } from '../../utils/getUserDevice';
import SiteMapping from '../../configs/siteMapping';
import breakPoint from '../../utils/constants';
import useCurrentPlatformUMStatus from '../../hooks/useCurrentPlatformUMStatus';
import { pushChangePlatformEventLog } from '../../utils/GrafanaFaro/pushEventLog';
import { useWindowDimensions } from '../../hooks/windowDimension';

const PlatformListItem = ({ item, getIconName, layoutType }) => {
    const { t } = useTranslation();
    const loginSelections = ['desktop', 'mobile'];
    const { siteName , ip } = useSiteInfo();
    const { width } = useWindowDimensions();
    const [state, dispatch] = useContext(HeaderContext);
    const [platformState, setPlatformState] = useState();
    const [showPlatform, setShowPlatform] = useState('');
    const [showPosition, setShowPosition] = useState('');
    const { fetchCurrentPlatUMData } = useCurrentPlatformUMStatus();

    useEffect(() => {
        setPlatformState(item.code === state.platform);
        const userDevice = getUserDevice();

        const platform = (() => {
            const isMobile = platformList.find((p) => p.code === userDevice)?.code === 'mobile';
            if (isMobile && width < breakPoint.mediumWidth) {
                return platformList.find((p) => p.code === 'mobile')?.abbreviation;
            }
            return width > breakPoint.mediumWidth
                ? platformList.find((p) => p.code === 'desktop')?.abbreviation
                : platformList.find((p) => p.code === 'mobile')?.abbreviation;
        })();

        setShowPlatform(platform);

        const foundPosition = appDownloadPosition.find((pos) => pos.layoutType === layoutType);
        if (foundPosition) {
            setShowPosition(foundPosition.position);
        }
    }, [state.platform, layoutType, width]);

    const handlePlatformClick = async (item) => {
        if (loginSelections.includes(item.code)) {
            const isCurrentPlatformUM = await fetchCurrentPlatUMData(item.code);
            if (isCurrentPlatformUM) {
                dispatch({ type: 'SHOW_UM_POPUP' });
            } else {
                dispatch({ type: 'CHANGE_PLATFORM', value: item.code });
            }
        }
        if (layoutType === 'rightNav') {
            dispatch({ type: 'TOGGLE_RIGHT_NAV_SHOWING', value: !state.isShowRightNav });
        }
        if (item.code === 'downloadapp') {
            gotoAppDownload(state.language, siteName, ip, showPlatform, showPosition);
        } else if (item.code === 'wap') {
            gotoWAP();
        }
    };

    return (
        <div
            key={item.code}
            className="platform-list__item"
            data-selected={platformState}
            onClick={() => handlePlatformClick(item)}
            siteName={siteName}
        >
            <a className="btn">
                <i className={`icon icon--${getIconName(item.code)}`} />
                <span className="text">{t(item.name)}</span>
                { item.code === 'downloadapp'
                    ? <i className="icon icon--arrow-right" /> : <i className="icon icon--done" />}
            </a>
        </div>
    );
};

const BDLoginPlatformListItem = ({ item, getIconName }) => {
    const { t } = useTranslation();
    const [state, dispatch] = useContext(HeaderContext);
    const [platformState, setPlatformState] = useState();

    useEffect(() => {
        setPlatformState(item.code === state.platform);
    }, [state.platform]);

    return (
        <div
            key={item.code}
            className="platform-list__item"
            data-selected={platformState}
            onClick={() => dispatch({ type: 'CHANGE_PLATFORM', value: item.code })}
        >
            <a
                className="btn"
            >
                <i className={`icon icon--${getIconName(item.code)}`} />
                <span className="text">{t(item.name)}</span>
                <i className="icon icon--done" />
            </a>
        </div>
    );
};

const PlatformContainer = forwardRef(({ layoutType, backdoorLogin }, ref) => {
    const { t } = useTranslation();
    const [state, dispatch] = useContext(HeaderContext);
    const {
        disableCTCD, disableDesktopCloud, siteName, appDownloadPageAndroid, appDownloadPageDesktop, appDownloadPageIOS,
    } = useSiteInfo();
    const [appDownloadVisible, setAppDownloadVisible] = useState(false);
    const [validPlatformList, setValidPlatformList] = useState(platformList);

    const configureAppDownloadSettings = (userDevice, deviceSystem) => {
        let layoutConfig = [];

        if (userDevice === 'desktop') {
            layoutConfig = appDownloadPageDesktop ? appDownloadPageDesktop.filter((item) => item === LAYOUT_POSITIONS.BEFORE_TOP || item === LAYOUT_POSITIONS.BEFORE_FOOTER) : [];

            setValidPlatformList((prevList) => prevList.filter((item) => item.code !== 'wap'));
        } else if (userDevice === 'mobile') {
            if (deviceSystem === 'iOS') {
                layoutConfig = appDownloadPageIOS ? appDownloadPageIOS.filter((item) => item === LAYOUT_POSITIONS.BEFORE_TOP || item === LAYOUT_POSITIONS.BEFORE_FOOTER) : [];
            } else if (deviceSystem === 'Android') {
                layoutConfig = appDownloadPageAndroid ? appDownloadPageAndroid.filter((item) => item === LAYOUT_POSITIONS.BEFORE_TOP || item === LAYOUT_POSITIONS.BEFORE_FOOTER) : [];
            }
        }

        if (layoutConfig && layoutConfig.length > 0) {
            setAppDownloadVisible(true);
        } else {
            setAppDownloadVisible(false); // 确保在没有有效项时设置为 false
        }
    };

    const getInitialPlatform = () => {
        let initialPlatform = getCookie('LOGIN_PLATFORM');

        if (initialPlatform === 'desktop') {
            return initialPlatform;
        }

        if (initialPlatform === 'mobile') {
            return initialPlatform;
        }

        initialPlatform = getUserDevice();

        setCookie('LOGIN_PLATFORM', initialPlatform, 3650);

        return initialPlatform;
    };

    useEffect(() => {
        dispatch({ type: 'CHANGE_PLATFORM', value: getInitialPlatform() });
        const userDevice = getUserDevice();
        const deviceSystem = detectOperatingSystem(navigator.userAgent);
        configureAppDownloadSettings(userDevice, deviceSystem);
    }, [dispatch]);

    const getPlatformList = () => {
        const withoutMobileBtn = [SiteMapping.id8899];
        const withWapBtn = [SiteMapping.Nova88, SiteMapping.IBCBet];
        const withoutAppDownLoadBtn = [
            SiteMapping.taraPlays,
            SiteMapping.bettha88,
            SiteMapping.fifabet,
            SiteMapping.ocean99,
        ];
        let list = validPlatformList;

        if (!appDownloadVisible || withoutAppDownLoadBtn.includes(siteName)) {
            list = list.filter((item) => item.code !== 'downloadapp');
        }
        if (!withWapBtn.includes(siteName)) {
            list = list.filter((item) => item.code !== 'wap');
        }
        if (withoutMobileBtn.includes(siteName)) {
            list = list.filter((item) => item.code !== 'mobile');
        }
        return list;
    };

    return (
        <div className="platform-list" ref={ref}>
            { layoutType === 'rightNav' && (
                <div className="platform-list__header">
                    <span className="text">{t('lbl_Platform')}</span>
                </div>
            )}

            {
                backdoorLogin
                    ? BDLoginPlatformList.map((item) => {
                        const getIconName = (code) => {
                            if (code === 'CTCD') return 'mobile';
                            if (code === 'desktopCloud') return 'desktop';
                            return code;
                        };

                        if (disableCTCD && item.code === 'CTCD') return [];
                        if (disableDesktopCloud && item.code === 'desktopCloud') return [];

                        return (
                            <BDLoginPlatformListItem key={item.code} item={item} getIconName={getIconName} />
                        );
                    })
                    : getPlatformList().map((item) => {
                        const getIconName = (code) => {
                            if (code === 'downloadapp') return 'download';
                            return code;
                        };

                        return (
                            <PlatformListItem
                                key={item.code}
                                item={item}
                                getIconName={getIconName}
                                layoutType={layoutType}
                            />
                        );
                    })
            }
        </div>
    );
});

export default PlatformContainer;
